import { useState, useRef, useEffect } from "react";
import { Col, Row, TableColumnsType, Tag, Button, Space } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";

import "./jobEvaluation.less";
import { paths } from "@router";
import {
  useDeleteJobEvaluationMutation,
  useFetchAllJobEvaluationsQuery,
} from "@services";
import Table from "@components/Table";
import AddJob from "./AddJob";
import { IJobEvaluationDataResponse } from "@/store/jobEvaluation/types";
import { dateFormat, JobEvaluationInquiryStatus } from "@/constants";
import moment, { isMoment } from "moment";
import { useTypedSelector } from "@/hooks";
import { handlePageChange, handlePageSizeChange, resetJobDetails } from "@/store/jobEvaluation";
import { useDispatch } from "react-redux";
import CustomButton from "@/components/customButton";
import SearchBox from "@/components/searchInput";
import JobCreationModal from "@/components/JobCreationModal";
import JobCreationTableComponent from "@components/jobCreationTable";
import UploadMassJobs from "@components/UploadMassJobs";
import { getTokenFromSharedStorage } from "@/services/cookies";
import { checkPermission } from "@/utils";
import { CREATE_EVALUATION, CREATE_JOB_EVALUATION, DELETE_JOB_EVALUATION, UPDATE_JOB_EVALUATION, VIEW_JOB_EVALUATION } from "@/router/permissions";

const Jobs = () => {
  const [deleteJobEvaluation] = useDeleteJobEvaluationMutation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [isVisible, setIsVisible] = useState(false);

  const [isMassUpload, setIsMassUpload] = useState(false);
  const { jobEvaluationListingPage, jobEvaluationPageSize } =
    useTypedSelector(state => state?.jobEvaluation);

  const { user, permissions } = useTypedSelector(state => state.auth);
  const company_id = user?.company?.id || 0;

  const per = permissions.filter(data => data === "CREATE_JOB_EVALUATION");

  const perUpdate = permissions.includes("UPDATE_JOB_EVALUATION") || permissions.includes("all");


  const perDelete = permissions.filter(
    data => data === "DELETE_JOB_EVALUATION" || data === "all"
  );
  const { data: jobEvaluationsData, isLoading, isFetching,
    refetch
  } =
    useFetchAllJobEvaluationsQuery({
      company_id,
      page: jobEvaluationListingPage,
      page_size: jobEvaluationPageSize,
    });

  const data = jobEvaluationsData?.data;
  const pagination = jobEvaluationsData?.pagination;

  const ref = useRef<HTMLDivElement>(null);
  // khurram's work
  const [filteredData, setFilteredData] = useState();
  const [searchInput, setSearchInput] = useState("");



  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const searchJobs = async (searchTerm: string): Promise<void> => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current); // Clear the previous timeout if it exists
    }

    debounceTimeout.current = setTimeout(async () => {
      const token = getTokenFromSharedStorage();
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/company/${company_id}/job-evaluations/search?search=${searchTerm}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token.access}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          console.error("Search API error:", errorData.message);
          return;
        }

        const data = await response.json();
        if (data) {
          setFilteredData(data.results); // Adjust according to the actual response structure
        } else {
          console.error("Search API error:", data);
        }
      } catch (error) {
        if (error instanceof Error) {
          console.error("Error:", error.message);
        } else {
          console.error("Unexpected error:", error);
        }
      }
    }, 300); // Adjust the debounce delay as necessary
  };

  const debounceTimeoutForActionDelete = {
    current: null as NodeJS.Timeout | null,
  };

  const debouncedApiCallDelete = (id: any, companyId: any, delay = 300) => {
    if (debounceTimeoutForActionDelete.current) {
      clearTimeout(debounceTimeoutForActionDelete.current); // Clear previous timeout
    }

    debounceTimeoutForActionDelete.current = setTimeout(async () => {
      try {
        await deleteJobEvaluation({
          company_id: companyId,
          job_evaluation_id: id,
        }).unwrap();
      } catch (error) {
        console.error("Error:", error instanceof Error ? error.message : error);
      }
    }, delay);
  };

  const debounceTimeoutForActionUpdate = {
    current: null as NodeJS.Timeout | null,
  };

  const debouncedApiCallUpdate = (id: any, companyId: any, delay = 300) => {
    if (debounceTimeoutForActionUpdate.current) {
      clearTimeout(debounceTimeoutForActionUpdate.current); // Clear previous timeout
    }

    debounceTimeoutForActionUpdate.current = setTimeout(async () => {
      const token = getTokenFromSharedStorage();
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/company/${companyId}/job-evaluations/${id}/`,
          {
            method: "PUT", // Update method
            headers: {
              Authorization: `Bearer ${token.access}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ action: "update" }), // Adjust payload if needed
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          return;
        }

        const data = await response.json();
      } catch (error) {
        console.error("Error:", error instanceof Error ? error.message : error);
      }
    }, delay);
  };

  const columns: TableColumnsType<IJobEvaluationDataResponse> = [
    {
      title: "Evaluation Id",
      defaultSortOrder: "ascend",
      dataIndex: "id",
      key: "id",
      // sorter: (a: any, b: any) => b?.id - a?.id,
      render: (_, record, index) => record?.id,
    },
    {
      title: "DATE",
      key: "updated_at",
      dataIndex: "created_at",
      sorter: (
        a: IJobEvaluationDataResponse,
        b: IJobEvaluationDataResponse
      ) => {
        return moment(a.updated_at).unix() - moment(b.updated_at).unix();
      },
      render: (record: any) => {
        return (
          <div className="text-wrap">
            {`${moment(record).format("DD-MMM-YYYY")}`}
          </div>
        );
      },
    },
    {
      title: "Job Title",
      dataIndex: "job_title",
      key: "job_title",
      sorter: (
        a: IJobEvaluationDataResponse,
        b: IJobEvaluationDataResponse
      ) => {
        return (a.job_title || "").localeCompare(b.job_title || "");
      },
    },
    {
      title: "Job Function",
      key: "job_function",
      dataIndex: ["job_function", "name"],
      sorter: (
        a: IJobEvaluationDataResponse,
        b: IJobEvaluationDataResponse
      ) => {
        const jobFunctionA = a.job_function?.name || "";
        const jobFunctionB = b.job_function?.name || "";
        return jobFunctionA.localeCompare(jobFunctionB);
      },
    },
    {
      title: "Country",
      key: "country",
      dataIndex: ["country", "name"],
      sorter: (
        a: IJobEvaluationDataResponse,
        b: IJobEvaluationDataResponse
      ) => {
        const jobFunctionA = a.country?.name || "";
        const jobFunctionB = b.country?.name || "";
        return jobFunctionA.localeCompare(jobFunctionB);
      },
    },
    {
      title: "Grade",
      key: "evaluation_result",
      width: 150,
      align: "center",
      sorter: (
        a: IJobEvaluationDataResponse,
        b: IJobEvaluationDataResponse
      ) => {
        const scoreA = a.evaluation_result || 0;
        const scoreB = b.evaluation_result || 0;
        return scoreA - scoreB;
      },
      render: (record: IJobEvaluationDataResponse) => {
        const score = record.evaluation_result ? record.evaluation_result : "-";
        return <div className="text-wrap">{score}</div>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      align: "center",
      // sorter: (a: IJobEvaluationDataResponse, b: IJobEvaluationDataResponse) => {
      //   const scoreA = a.evaluation_result || 0;
      //   const scoreB = b.evaluation_result || 0;
      //   return scoreA - scoreB;
      // },
      render: (record: IJobEvaluationDataResponse) => {
        const { id, company }: any = record;
        const companyId = company?.id;
        const status = record?.status?.display_name

        return (
          <div className="actions flex items-center justify-center">
            {/* {(user?.role === "TOM_MASTER_USER" || perDelete.length > 0) && ( */}

            <div className="flex justify-between gap-2">
              <>
                {status?.toLowerCase() == JobEvaluationInquiryStatus.EVALUTAED || status?.toLowerCase() == JobEvaluationInquiryStatus.CLOSE ? <Button
                  className="flex justify-center items-center"

                  onClick={() => history.push(paths.client.jobEvaluation.summary(id.toString()))}
                >
                  <EyeOutlined />
                </Button> : null}
              </>
              <>{checkPermission(UPDATE_JOB_EVALUATION) &&
                (status?.toLowerCase() === JobEvaluationInquiryStatus.OPEN || status?.toLowerCase() == JobEvaluationInquiryStatus.EVALUTAED ? <Button
                  onClick={() => history.push(paths.client.jobEvaluation.create(id.toString()))}
                  className="flex justify-center items-center"
                >
                  <EditOutlined style={{ color: "#00519d", fontSize: "16px" }} />
                </Button> : null)
              }</>
              <>
                {checkPermission(DELETE_JOB_EVALUATION) && <Button
                  onClick={() => debouncedApiCallDelete(id, companyId)}
                  className="flex justify-center items-center"

                >
                  <DeleteOutlined style={{ color: "red", fontSize: "16px" }} />
                </Button>}
              </>

            </div>
            {/* )
            } */}
          </div >
        );
      },
    },

    // {
    //   title: "Job Evaluation Status",
    //   // dataIndex: "status",
    //   key: "status",
    //   align: "center",
    //   render: (record: IJobEvaluationDataResponse) => {
    //     const color: { [key: string]: string } = {
    //       open: "green",
    //       "in progress": "gold",
    //       close: "red",
    //     };
    //     const key = record.status?.key!;
    //     return <Tag color={color[key.toLowerCase()]}>{key.toUpperCase()}</Tag>;
    //   },
    // },
  ];

  const onRowClick = (data: IJobEvaluationDataResponse) => {
    const jobId = data?.id ? data?.id?.toString() : "";
    data.status?.key === JobEvaluationInquiryStatus.CLOSE
      ? history.push(paths.client.jobEvaluation.summary(jobId))
      : history.push(paths.client.jobEvaluation.create(jobId));
  };
  const onCloseModal = () => {
    setIsVisible(false);
  };
  const onCloseMassModal = () => {
    setIsMassUpload(false);
  };
  const handleTablePageChange = (page: number, pageSize?: number) => {
    if (pageSize && pageSize !== jobEvaluationPageSize) {
      dispatch(handlePageSizeChange(pageSize));
    } else {
      dispatch(handlePageChange(page));
    }
  };



  useEffect(() => {
    refetch()
    if (ref.current) {
      ref.current.scrollTop = 0;
    }
  }, [])



  console.log(checkPermission(UPDATE_JOB_EVALUATION), checkPermission(DELETE_JOB_EVALUATION), checkPermission(CREATE_JOB_EVALUATION))
  return (
    <div className="bg-themeCore min-h-screen h-full" ref={ref} style={{ height: "400px", overflowY: "auto" }} >
      <div className="appTitle text-base font-[500] mx-5">Job Creation</div>
      <div className="jobCreationBar sticky top-0 z-10 bg-white px-14 py-6 rounded-lg flex justify-between items-center my-7">
        <div className="title text-themeBlue font-[500] text-3xl">
          Job Creation
        </div>

        <div className="flex items-center">
          {(user?.role === "COMPANY_SUPER_USER" || user?.role === "TOM_MASTER_USER" || per.length > 0) && (
            <>
              <CustomButton
                onClick={() => {
                  dispatch(resetJobDetails);
                  setIsVisible(true);
                }}
                btnName="Create Individual Job"
                customClasses="shadow-lg"
              />

              <CustomButton
                onClick={() => {
                  dispatch(resetJobDetails);
                  setIsMassUpload(true);
                }}
                btnName="Create Mass Upload"
                customClasses="shadow-lg ml-6"
              />
            </>
          )}
        </div>
      </div>

      <div className="jobCreationListing bg-white px-14 py-6 rounded-lg">

        <SearchBox
          placeholder="Type to search..."
          value={searchInput}
          // onChange={value => setSearchInput(value)}
          onChange={value => {
            setSearchInput(value);
            searchJobs(value);
          }} // khurram's work
        />

        <JobCreationTableComponent
          onRowClick={onRowClick}
          columns={columns}
          data={filteredData ?? data}
          isLoading={isLoading || isFetching}
          pagination={true}
          count={pagination?.count}
          onChangePage={handleTablePageChange}
          page={jobEvaluationListingPage}
          perUpdate={perUpdate}
          perDelete={perDelete}
          user={user?.role}
          pageSize={jobEvaluationPageSize}
        />

        <JobCreationModal visible={isVisible} onClose={onCloseModal} />
        <UploadMassJobs visible={isMassUpload} onClose={onCloseMassModal} />

      </div>
    </div>
  );
};

export default Jobs;